import { useEffect, useRef } from "react";

// Floating ember particle layer (pure canvas, respects reduced motion)
export default function EmberCanvas({ density = 46, className = "" }) {
  const ref = useRef(null);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    const canvas = ref.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    let raf;
    let w = 0;
    let h = 0;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    const resize = () => {
      const rect = canvas.parentElement.getBoundingClientRect();
      w = rect.width;
      h = rect.height;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    const spawn = (initial) => ({
      x: Math.random() * w,
      y: initial ? Math.random() * h : h + 10,
      r: 0.6 + Math.random() * 2.1,
      vy: 0.25 + Math.random() * 0.75,
      vx: (Math.random() - 0.5) * 0.3,
      life: 0.35 + Math.random() * 0.6,
      gold: Math.random() < 0.28,
      flicker: Math.random() * Math.PI * 2,
    });

    const particles = Array.from({ length: density }, () => spawn(true));

    const tick = () => {
      ctx.clearRect(0, 0, w, h);
      for (let i = 0; i < particles.length; i += 1) {
        const p = particles[i];
        p.y -= p.vy;
        p.x += p.vx + Math.sin(p.flicker + p.y * 0.01) * 0.15;
        if (p.y < -12 || p.x < -12 || p.x > w + 12) particles[i] = spawn(false);
        const alpha = p.life * (0.55 + 0.45 * Math.sin(p.flicker + p.y * 0.02));
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = p.gold
          ? `rgba(250, 204, 21, ${Math.max(alpha, 0)})`
          : `rgba(255, 30, 39, ${Math.max(alpha, 0)})`;
        ctx.shadowColor = p.gold ? "rgba(250,204,21,0.8)" : "rgba(255,30,39,0.8)";
        ctx.shadowBlur = 6;
        ctx.fill();
        ctx.shadowBlur = 0;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, [density]);

  return <canvas ref={ref} aria-hidden="true" className={`pointer-events-none absolute inset-0 ${className}`} />;
}
